import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSocket } from "@/context/SocketContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronLeft, Send } from "lucide-react";
import { EmptyState } from "@/components/common/EmptyState";
import { MessageSquare } from "lucide-react";

export default function ChatThread() {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const socket = useSocket();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const scroller = useRef(null);

  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then((r) => setBooking(r.data.booking));
    api.get(`/bookings/${bookingId}/messages`).then((r) => setMessages(r.data.items || []));
  }, [bookingId]);

  useEffect(() => {
    if (!socket) return;
    const off = socket.on((msg) => {
      if (msg?.event === "chat_message" && msg.data?.booking_id === bookingId) {
        setMessages((prev) => {
          if (prev.find((m) => m.id === msg.data.message.id)) return prev;
          return [...prev, msg.data.message];
        });
      }
    });
    return off;
  }, [socket, bookingId]);

  useEffect(() => {
    scroller.current?.scrollTo?.({ top: 1e6, behavior: "smooth" });
  }, [messages.length]);

  async function send(e) {
    e.preventDefault();
    if (!text.trim()) return;
    const { data } = await api.post(`/bookings/${bookingId}/messages`, { text });
    setMessages((prev) => prev.find((m) => m.id === data.message.id) ? prev : [...prev, data.message]);
    setText("");
  }

  const other = booking && (user?.role === "athlete" ? booking.ballboy : booking.athlete);

  return (
    <div className="flex flex-col h-[calc(100dvh-200px)]" data-testid="chat-thread">
      <div className="flex items-center gap-2 mb-2">
        <Button variant="ghost" size="icon" onClick={() => nav(-1)}><ChevronLeft className="h-5 w-5" /></Button>
        <div>
          <div className="text-sm text-muted-foreground">Chat</div>
          <div className="font-semibold bb-display">{other?.name || "Waiting for match…"}</div>
        </div>
        <div className="ml-auto">
          <Link to={`/bookings/${bookingId}`} className="text-xs text-primary underline">Booking</Link>
        </div>
      </div>
      <div ref={scroller} className="flex-1 overflow-y-auto rounded-2xl bg-muted/40 p-3 space-y-2">
        {messages.length === 0 ? (
          <EmptyState icon={MessageSquare} title="Say hi!" description="Messages you send here are private to this booking." />
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === user?.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                    mine ? "bg-primary text-primary-foreground rounded-br-md" : "bg-card text-foreground border border-border rounded-bl-md"
                  }`}
                >
                  <div>{m.text}</div>
                  <div className={`text-[10px] mt-1 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                    {new Date(m.created_at).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <form onSubmit={send} className="mt-2 flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message" data-testid="chat-message-input" />
        <Button type="submit" disabled={!text.trim() || !booking?.ballboy_id || !booking?.athlete_id} data-testid="chat-send-button">
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}
